import React, { useState, useEffect } from 'react';
import { api } from '../../api/api';

interface Asset {
  id: number;
  name: string;
  description: string;
  price: number;
  image_path: string;
}

interface UpdateAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetUpdated: () => void;
  asset: Asset | null;
}

const UpdateAssetModal: React.FC<UpdateAssetModalProps> = ({ isOpen, onClose, onAssetUpdated, asset }) => {
  const [updatedAsset, setUpdatedAsset] = useState<Asset | null>(asset);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    setUpdatedAsset(asset);
  }, [asset]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUpdatedAsset(prevAsset => (prevAsset ? { ...prevAsset, [name]: value } : null));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatedAsset) return;

    let assetData = { ...updatedAsset };

    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        const uploadResponse = await api.post('/api/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        assetData.image_path = uploadResponse.data.filename;
      } catch (uploadError) {
        console.error('Failed to upload file', uploadError);
        alert('Failed to upload image.');
        return;
      }
    }

    try {
      await api.patch(`/api/all/assets/${assetData.id}`, assetData);
      alert('Asset updated successfully!');
      onAssetUpdated();
    } catch (error) {
      console.error('Failed to update asset', error);
      alert('Failed to update asset.');
    }
  };

  if (!isOpen || !updatedAsset) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-md border-2 border-gray-700">
        <h2 className="text-3xl font-bold text-white mb-6 text-center">Update Asset</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-white text-lg font-bold mb-2">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={updatedAsset.name}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="description" className="block text-white text-lg font-bold mb-2">Description:</label>
            <textarea
              id="description"
              name="description"
              value={updatedAsset.description}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600 h-24"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="price" className="block text-white text-lg font-bold mb-2">Price:</label>
            <input
              type="number"
              id="price"
              name="price"
              value={updatedAsset.price}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600"
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="imageFile" className="block text-white text-lg font-bold mb-2">Image File:</label>
            <input
              type="file"
              id="imageFile"
              name="imageFile"
              accept="image/*"
              onChange={handleFileChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-600"
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
            >
              Update Asset
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateAssetModal;
